import { useState, useEffect, useCallback } from 'react'
import { RefreshCw, Search, Zap, ExternalLink } from 'lucide-react'
import { clsx } from 'clsx'
import { formatDistanceToNow } from 'date-fns'
import { api } from '../lib/api'

const ENERGY_KEYWORDS = [
  'energy', 'power', 'grid', 'solar', 'wind', 'nuclear', 'fusion', 'battery',
  'ev', 'renewable', 'carbon', 'sustainability', 'green', 'pue',
  'data centre power', 'data center power', 'cooling', 'hydrogen',
]

function relativeTime(ts: string): string {
  if (!ts) return ''
  try { return formatDistanceToNow(new Date(ts), { addSuffix: false }) + ' ago' } catch { return ts }
}

function isWithin24h(ts: string): boolean {
  if (!ts) return false
  try {
    return new Date(ts).getTime() > Date.now() - 24 * 60 * 60 * 1000
  } catch {
    return false
  }
}

export default function EnergyPage() {
  const [items, setItems] = useState<any[]>([])
  const [searchTerm, setSearchTerm] = useState('')
  const [loading, setLoading] = useState(true)

  const fetchData = useCallback(async () => {
    setLoading(true)
    try {
      const [newsRes, supplyRes] = await Promise.allSettled([
        api.get('/api/news/feed', { params: { limit: 80 } }),
        api.get('/api/supply-chain/feed', { params: { limit: 40 } }),
      ])

      const newsArticles = newsRes.status === 'fulfilled'
        ? (newsRes.value.data.articles || newsRes.value.data.items || newsRes.value.data || [])
        : []
      const supplyItems = supplyRes.status === 'fulfilled'
        ? (supplyRes.value.data.articles || supplyRes.value.data.items || supplyRes.value.data || [])
        : []

      // Filter news for energy keywords
      const energyNews = newsArticles.filter((a: any) => {
        const text = `${a.title || ''} ${a.summary || ''} ${a.description || ''}`.toLowerCase()
        return ENERGY_KEYWORDS.some(kw => text.includes(kw))
      })

      // Filter supply chain for energy category or keywords
      const energySupply = supplyItems.filter((a: any) => {
        if ((a.category || '').toLowerCase() === 'energy') return true
        const text = `${a.title || ''} ${a.summary || ''} ${a.description || ''}`.toLowerCase()
        return ENERGY_KEYWORDS.some(kw => text.includes(kw))
      })

      // Combine and deduplicate
      const all = [...energyNews, ...energySupply]
      const seen = new Set<string>()
      const unique = all.filter(a => {
        const key = (a.title || '').toLowerCase().trim()
        if (seen.has(key)) return false
        seen.add(key)
        return true
      })

      unique.sort((a, b) =>
        new Date(b.timestamp || b.published || 0).getTime() - new Date(a.timestamp || a.published || 0).getTime()
      )

      setItems(unique)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { fetchData(); const i = setInterval(fetchData, 120_000); return () => clearInterval(i) }, [fetchData])

  const filtered = items.filter(item => {
    if (!searchTerm) return true
    const text = `${item.title || ''} ${item.summary || ''} ${item.source || ''}`.toLowerCase()
    return text.includes(searchTerm.toLowerCase())
  })

  const last24hCount = items.filter(a => isWithin24h(a.timestamp || a.published)).length

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-text-primary flex items-center gap-3">
            <Zap size={24} className="text-emerald" /> Energy &amp; Sustainability
          </h1>
          <p className="text-sm text-text-secondary mt-1">Data centre power, green tech, energy markets</p>
        </div>
        <button type="button" onClick={fetchData} className="btn-secondary text-xs">
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} /> Refresh
        </button>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="stat-card">
          <p className="stat-label">Articles (24h)</p>
          <p className="stat-value">{last24hCount}</p>
        </div>
        <div className="stat-card">
          <p className="stat-label">Total Articles</p>
          <p className="stat-value">{items.length}</p>
        </div>
        <div className="stat-card">
          <p className="stat-label">Sources</p>
          <p className="stat-value">{new Set(items.map(a => a.source).filter(Boolean)).size}</p>
        </div>
        <div className="stat-card">
          <p className="stat-label">Keywords Tracked</p>
          <p className="stat-value">{ENERGY_KEYWORDS.length}</p>
        </div>
      </div>

      <div className="relative max-w-sm">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted" />
        <input
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          placeholder="Search energy articles..."
          className="input pl-9 py-2 text-xs"
        />
      </div>

      {loading && !items.length && (
        <div className="flex items-center justify-center h-32">
          <RefreshCw size={20} className="animate-spin text-text-muted" />
          <span className="ml-2 text-text-muted text-sm">Scanning feeds for energy content...</span>
        </div>
      )}

      <div className="space-y-3">
        {filtered.map((item, idx) => (
          <div key={item.url || idx} className="card card-hover p-4">
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="badge-green text-[10px]">ENERGY</span>
                  <span className="text-[10px] text-text-muted">{item.source}</span>
                  <span className="text-[10px] text-text-muted">&middot;</span>
                  <span className="text-[10px] text-text-muted">{relativeTime(item.timestamp || item.published)}</span>
                </div>
                <h3 className="text-sm font-medium text-text-primary leading-snug">{item.title}</h3>
                {(item.summary || item.description) && (
                  <p className="text-xs text-text-secondary mt-1 line-clamp-2">{item.summary || item.description}</p>
                )}
              </div>
              {item.url && (
                <a href={item.url} target="_blank" rel="noopener" className="text-text-muted hover:text-text-primary flex-shrink-0">
                  <ExternalLink size={14} />
                </a>
              )}
            </div>
          </div>
        ))}
      </div>

      {!loading && filtered.length === 0 && (
        <div className="card p-8 text-center">
          <p className="text-sm text-text-muted">No energy articles found matching your search.</p>
        </div>
      )}
    </div>
  )
}
